import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { productsApi, productHistoryApi, artificeTypesApi } from '../lib/api';
import api, { fetchBlob } from '../lib/api';
import LabelPrintDialog from '../components/LabelPrintDialog';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Search, Package, Filter, Upload, 
  Edit, Trash2, Eye, FileText, QrCode, MoreVertical,
  Shield, Flame, Timer, Ruler, DollarSign, Weight, Target, Printer, History,
  ArrowUpDown, ArrowUp, ArrowDown, X, Copy, AlertTriangle, Zap
} from 'lucide-react';
import { getApiErrorMessage } from '../lib/errorUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const CATEGORIES = ['F1', 'F2', 'F3', 'F4', 'T1', 'T2', 'P1', 'P2'];
const STORAGE_CLASSES = ['1.1G', '1.2G', '1.3G', '1.4G', '1.4S'];

const getCategoryBadgeClass = (category) => {
  const classes = {
    F1: 'badge-f1', F2: 'badge-f2', F3: 'badge-f3', F4: 'badge-f4',
    T1: 'badge-t1', T2: 'badge-t2', P1: 'badge-p1', P2: 'badge-p2',
  };
  return classes[category] || '';
};

const getRiskBadgeClass = (risk) => {
  const classes = {
    '1.1G': 'bg-red-500/20 text-red-400 border-red-500/30',
    '1.2G': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    '1.3G': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    '1.4G': 'bg-green-500/20 text-green-400 border-green-500/30',
    '1.4S': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  };
  return classes[risk] || '';
};

const emptyForm = {
  reference: '', designation: '', designation_short: '', type_artifice: '',
  family: '', sub_family: '', caliber: '', shots_count: '', duration: '',
  safety_distance: '', category: '', risk_class: '', un_code: '',
  nec: '', gross_weight: '', net_weight: '', dimensions: '', supplier: '',
  manufacturer: '', country_origin: '', pieces_per_box: '',
  alert_threshold: '', homologation_number: '', purchase_price: '',
  public_price: '', fds_url: '',
};

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [riskClassFilter, setRiskClassFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('reference');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [labelItems, setLabelItems] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [editMode, setEditMode] = useState(false);
  const [editProductId, setEditProductId] = useState(null);
  const [artificeTypes, setArtificeTypes] = useState([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [showManageTypes, setShowManageTypes] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicateData, setDuplicateData] = useState(null);
  const [duplicateIds, setDuplicateIds] = useState(new Set());
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [incompleteFilter, setIncompleteFilter] = useState(false);
  const [incompleteCount, setIncompleteCount] = useState(0);
  const [showAutoMap, setShowAutoMap] = useState(false);
  const [autoMapLoading, setAutoMapLoading] = useState(false);
  const [autoMapPreview, setAutoMapPreview] = useState(null);
  const [autoMapApplying, setAutoMapApplying] = useState(false);

  // Load artifice types
  useEffect(() => {
    artificeTypesApi.getAll().then(res => setArtificeTypes(res.data || [])).catch(() => {});
  }, []);

  const handleAddArtificeType = async () => {
    const name = newTypeName.trim();
    if (!name) return;
    try {
      await artificeTypesApi.create(name);
      setArtificeTypes(prev => [...prev, name].sort());
      setNewTypeName('');
      toast.success(`Type "${name}" ajouté`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur'));
    }
  };

  const handleDeleteArtificeType = async (name) => {
    if (!window.confirm(`Supprimer le type "${name}" ?`)) return;
    try {
      await artificeTypesApi.delete(name);
      setArtificeTypes(prev => prev.filter(t => t !== name));
      toast.success(`Type "${name}" supprimé`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur'));
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [searchQuery, categoryFilter, riskClassFilter, typeFilter, sortBy, sortOrder, page, incompleteFilter]);

  // Fetch incomplete count on load and after product changes
  const fetchIncompleteCount = useCallback(() => {
    api.get('/products/incomplete-count').then(res => setIncompleteCount(res.data?.count || 0)).catch(() => {});
  }, []);

  useEffect(() => { fetchIncompleteCount(); }, [fetchIncompleteCount]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = { limit: pageSize, skip: (page - 1) * pageSize, sort_by: sortBy, sort_order: sortOrder };
      if (searchQuery) params.search = searchQuery;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (riskClassFilter !== 'all') params.risk_class = riskClassFilter;
      if (typeFilter !== 'all') params.type_artifice = typeFilter;
      if (incompleteFilter) params.incomplete = true;
      const response = await productsApi.getAll(params);
      setProducts(response.data.items || []);
      setTotal(response.data.total || 0);
      fetchIncompleteCount();
    } catch (error) {
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  // Reset page on filter/sort change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, categoryFilter, riskClassFilter, typeFilter, sortBy, sortOrder, incompleteFilter]);

  const fetchDuplicates = async () => {
    setDuplicateLoading(true);
    try {
      const res = await api.get('/products/duplicates');
      setDuplicateData(res.data);
      setDuplicateIds(new Set(res.data.duplicate_ids || []));
      setShowDuplicates(true);
      if (res.data.total_duplicates === 0) {
        toast.success('Aucun doublon detecte !');
      }
    } catch {
      toast.error('Erreur lors de la detection des doublons');
    }
    setDuplicateLoading(false);
  };

  const openAutoMapDialog = async () => {
    setShowAutoMap(true);
    setAutoMapPreview(null);
    setAutoMapLoading(true);
    try {
      const res = await api.post('/products/auto-map-risk-class', { dry_run: true, force: true });
      setAutoMapPreview(res.data);
    } catch (err) {
      toast.error('Impossible de calculer l\'aperçu');
      setShowAutoMap(false);
    } finally {
      setAutoMapLoading(false);
    }
  };

  const applyAutoMap = async () => {
    setAutoMapApplying(true);
    try {
      const res = await api.post('/products/auto-map-risk-class', { dry_run: false, force: true });
      toast.success(`${res.data.applied} article(s) mis à jour`);
      setShowAutoMap(false);
      setAutoMapPreview(null);
      fetchProducts();
    } catch (err) {
      toast.error('Erreur lors de l\'application');
    } finally {
      setAutoMapApplying(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-amber-400" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-amber-400" />;
  };

  const hasActiveFilters = categoryFilter !== 'all' || riskClassFilter !== 'all' || typeFilter !== 'all' || searchQuery;

  const clearAllFilters = () => {
    setCategoryFilter('all');
    setRiskClassFilter('all');
    setTypeFilter('all');
    setSearchQuery('');
    setSortBy('reference');
    setSortOrder('asc');
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    // Preserve scroll position across edits so the user stays on the same row
    const scrollY = window.scrollY;
    try {
      const data = { ...formData };
      ['shots_count', 'pieces_per_box', 'alert_threshold', 'duration', 'safety_distance'].forEach(k => {
        if (data[k]) data[k] = parseInt(data[k]);
      });
      ['nec', 'gross_weight', 'net_weight', 'purchase_price', 'public_price'].forEach(k => {
        if (data[k]) data[k] = parseFloat(data[k]);
      });
      // NEC and gross_weight are displayed in grams, convert to kg for storage
      if (data.nec) data.nec = data.nec / 1000;
      if (data.gross_weight) data.gross_weight = data.gross_weight / 1000;
      if (data.caliber && !isNaN(data.caliber)) data.caliber = parseInt(data.caliber);
      // Sync packaging with pieces_per_box for backward compatibility
      if (data.pieces_per_box) data.packaging = String(data.pieces_per_box);
      Object.keys(data).forEach(key => { if (data[key] === '') delete data[key]; });

      if (editMode && editProductId) {
        await productsApi.update(editProductId, data);
        toast.success('Produit mis à jour');
      } else {
        await productsApi.create(data);
        toast.success('Produit créé avec succès');
      }
      setShowCreateDialog(false);
      await fetchProducts();
      resetForm();
      // Restore scroll position after the list re-renders
      requestAnimationFrame(() => { window.scrollTo({ top: scrollY, behavior: 'instant' }); });
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : 'Erreur lors de la sauvegarde';
      toast.error(msg);
    }
  };

  const handleViewProduct = async (product) => {
    try {
      const response = await productsApi.getById(product.id);
      setSelectedProduct(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Erreur lors du chargement du produit');
    }
  };

  const handleEditProduct = (product) => {
    setEditMode(true);
    setEditProductId(product.id);
    setFormData({
      reference: product.reference || '',
      designation: product.designation || '',
      designation_short: product.designation_short || '',
      type_artifice: product.type_artifice || '',
      family: product.family || '',
      sub_family: product.sub_family || '',
      caliber: product.caliber != null ? String(product.caliber) : '',
      shots_count: product.shots_count != null ? String(product.shots_count) : '',
      duration: product.duration != null ? String(product.duration) : '',
      safety_distance: product.safety_distance != null ? String(product.safety_distance) : '',
      category: product.category || '',
      risk_class: product.risk_class || '',
      un_code: product.un_code || '',
      nec: product.nec != null ? String(Math.round(product.nec * 1000 * 100) / 100) : '',
      gross_weight: product.gross_weight != null ? String(Math.round(product.gross_weight * 1000 * 100) / 100) : '',
      net_weight: product.net_weight != null ? String(product.net_weight) : '',
      dimensions: product.dimensions || '',
      supplier: product.supplier || '',
      manufacturer: product.manufacturer || '',
      country_origin: product.country_origin || '',
      pieces_per_box: product.pieces_per_box != null ? String(product.pieces_per_box) : (product.packaging != null ? String(product.packaging) : ''),
      alert_threshold: product.alert_threshold != null ? String(product.alert_threshold) : '',
      homologation_number: product.homologation_number || '',
      purchase_price: product.purchase_price != null ? String(product.purchase_price) : '',
      public_price: product.public_price != null ? String(product.public_price) : '',
      fds_url: product.fds_url || '',
    });
    setShowCreateDialog(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await productsApi.delete(productId);
      toast.success('Article supprimé');
      fetchProducts();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 400 && detail.includes('stock')) {
        if (window.confirm(`${detail}\n\nVoulez-vous FORCER la suppression ?\nL'article ET tout son stock seront définitivement supprimés.`)) {
          try {
            const res = await productsApi.delete(productId, true);
            toast.success(res.data?.message || 'Article et stock supprimés');
            fetchProducts();
          } catch (e2) {
            toast.error(getApiErrorMessage(e2, 'Erreur lors de la suppression forcée'));
          }
        }
      } else {
        toast.error(detail || 'Erreur lors de la suppression');
      }
    }
  };

  const handleViewHistory = async (productId) => {
    setHistoryLoading(true);
    setShowHistory(true);
    try {
      const res = await productHistoryApi.get(productId);
      setHistoryData(res.data);
    } catch {
      toast.error('Erreur chargement historique');
      setShowHistory(false);
    }
    setHistoryLoading(false);
  };

  const handleDownloadQR = useCallback(async (product) => {
    try {
      const res = await fetchBlob('GET', `/qrcode/product/${product.id}`);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `QR_${product.reference || product.id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`QR Code telecharge pour ${product.reference}`);
    } catch {
      toast.error('Erreur lors du telechargement du QR Code');
    }
  }, []);

  const resetForm = () => {
    setFormData({ ...emptyForm });
    setEditMode(false);
    setEditProductId(null);
  };

  const f = (key, val) => {
    const newForm = { ...formData, [key]: val };
    // Auto-compute safety distance based on category rule
    if (key === 'category' || key === 'caliber') {
      const cat = key === 'category' ? val : newForm.category;
      const cal = key === 'caliber' ? val : newForm.caliber;
      if (cat === 'F1') newForm.safety_distance = '1';
      else if (cat === 'F2') newForm.safety_distance = '8';
      else if (cat === 'F3') newForm.safety_distance = '25';
      else if (cat === 'F4' && cal && parseInt(cal) > 0) newForm.safety_distance = String(parseInt(cal));
    }
    setFormData(newForm);
  };

  const DetailRow = ({ label, value, mono, accent }) => (
    <div>
      <p className="text-xs text-zinc-500 mb-0.5">{label}</p>
      <p className={`text-sm ${mono ? 'font-mono' : ''} ${accent ? accent : 'text-white'}`}>
        {value || '-'}
      </p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Base Articles</h1>
          <p className="text-zinc-400 text-sm mt-1">{total} produits pyrotechniques</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" 
            onClick={() => setIncompleteFilter(!incompleteFilter)}
            className={`border-zinc-700 text-zinc-300 hover:text-white ${incompleteFilter ? 'border-red-500/50 bg-red-500/10 text-red-400' : incompleteCount > 0 ? 'border-orange-500/30 text-orange-400' : ''}`}
            data-testid="incomplete-filter-button">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Incomplets {incompleteCount > 0 && <Badge variant="outline" className="ml-1.5 text-[10px] bg-red-500/20 text-red-400 border-red-500/30 px-1.5">{incompleteCount}</Badge>}
          </Button>
          <Button variant="outline" onClick={fetchDuplicates} disabled={duplicateLoading}
            className={`border-zinc-700 text-zinc-300 hover:text-white hover:border-amber-500/50 ${duplicateIds.size > 0 ? 'border-orange-500/50 text-orange-400' : ''}`}
            data-testid="detect-duplicates-button">
            {duplicateLoading ? (
              <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Copy className="w-4 h-4 mr-2" />
            )}
            Doublons {duplicateIds.size > 0 && <Badge variant="outline" className="ml-1.5 text-[10px] bg-orange-500/20 text-orange-400 border-orange-500/30 px-1.5">{duplicateIds.size}</Badge>}
          </Button>
          <Button variant="outline" onClick={openAutoMapDialog}
            className="border-zinc-700 text-zinc-300 hover:text-white hover:border-cyan-500/50"
            data-testid="auto-map-adr-button"
            title="Appliquer la règle ADR : le code ONU détermine la classe de risque (ex: UN0335 → 1.3G)">
            <Zap className="w-4 h-4 mr-2" />
            Auto-classe ADR
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="create-product-button">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Produit
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white font-heading">
                  {editMode ? 'Modifier le Produit' : 'Nouveau Produit Pyrotechnique'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmitProduct} className="space-y-6">
                <Tabs defaultValue="general" className="w-full">
                  <TabsList className="bg-zinc-800 w-full justify-start">
                    <TabsTrigger value="general">Général</TabsTrigger>
                    <TabsTrigger value="technical">Technique</TabsTrigger>
                    <TabsTrigger value="pyro">Pyrotechnie</TabsTrigger>
                    <TabsTrigger value="commercial">Commercial</TabsTrigger>
                    <TabsTrigger value="documents">Documents</TabsTrigger>
                  </TabsList>
                  
                  {/* GENERAL TAB */}
                  <TabsContent value="general" className="space-y-4 mt-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Référence *</Label>
                        <Input value={formData.reference} onChange={(e) => f('reference', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white font-mono" placeholder="Référence" required data-testid="product-reference-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Catégorie *</Label>
                        <Select value={formData.category} onValueChange={(v) => f('category', v)}>
                          <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white" data-testid="product-category-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {CATEGORIES.map(cat => <SelectItem key={cat} value={cat} className="text-white">{cat}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-zinc-300">Type d'artifice</Label>
                          <Button type="button" variant="ghost" size="sm" className="text-zinc-500 hover:text-amber-400 h-6 px-1.5 text-xs"
                            onClick={() => setShowManageTypes(true)} data-testid="manage-types-button">
                            Gérer les types
                          </Button>
                        </div>
                        <Select value={formData.type_artifice} onValueChange={(v) => f('type_artifice', v)}>
                          <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {artificeTypes.map(t => <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Désignation *</Label>
                      <Input value={formData.designation} onChange={(e) => f('designation', e.target.value)}
                        className="bg-zinc-950 border-zinc-800 text-white" placeholder="Désignation complète" required data-testid="product-designation-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Désignation courte</Label>
                      <Input value={formData.designation_short} onChange={(e) => f('designation_short', e.target.value)}
                        className="bg-zinc-950 border-zinc-800 text-white" placeholder="Nom court pour étiquettes" />
                    </div>
                  </TabsContent>

                  {/* TECHNICAL TAB */}
                  <TabsContent value="technical" className="space-y-4 mt-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Classe ADR *</Label>
                        <Select value={formData.risk_class} onValueChange={(v) => f('risk_class', v)}>
                          <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {STORAGE_CLASSES.map(cls => (
                              <SelectItem key={cls} value={cls} className="text-white">
                                <span className={cls.startsWith('1.1') ? 'text-red-400' : cls.startsWith('1.2') ? 'text-orange-400' : cls.startsWith('1.3') ? 'text-amber-400' : 'text-green-400'}>{cls}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Code ONU</Label>
                        <Input value={formData.un_code} onChange={(e) => f('un_code', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white font-mono" placeholder="Code ONU" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">NEC (g)</Label>
                        <Input type="number" step="0.01" value={formData.nec} onChange={(e) => f('nec', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white font-mono" placeholder="NEC en grammes" />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Poids brut (g)</Label>
                        <Input type="number" step="0.01" value={formData.gross_weight} onChange={(e) => f('gross_weight', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white" placeholder="Poids brut en grammes" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Dimensions</Label>
                        <Input value={formData.dimensions} onChange={(e) => f('dimensions', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white" placeholder="L x l x H" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Pièces par carton</Label>
                        <Input type="number" value={formData.pieces_per_box} onChange={(e) => f('pieces_per_box', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white" placeholder="Nombre" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Seuil d'alerte stock</Label>
                        <Input type="number" value={formData.alert_threshold} onChange={(e) => f('alert_threshold', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white" placeholder="Seuil minimum" />
                      </div>
                    </div>
                  </TabsContent>

                  {/* PYROTECHNIE TAB */}
                  <TabsContent value="pyro" className="space-y-4 mt-4">
                    <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30 text-sm text-zinc-400">
                      <p className="text-amber-400 font-semibold mb-1">Calcul auto distance de sécurité</p>
                      <p>F1 = 1m | F2 = 8m | F3 = 25m | F4 = selon FDS, sinon 1mm calibre = 1m</p>
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Calibre (mm)</Label>
                        <Input type="number" value={formData.caliber} onChange={(e) => f('caliber', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white" placeholder="Calibre en mm" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Nombre de coups</Label>
                        <Input type="number" value={formData.shots_count} onChange={(e) => f('shots_count', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white" placeholder="Nombre de coups" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Durée (secondes)</Label>
                        <Input type="number" value={formData.duration} onChange={(e) => f('duration', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white" placeholder="Durée en sec." />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Distance sécurité (m)</Label>
                        <Input type="number" value={formData.safety_distance} onChange={(e) => f('safety_distance', e.target.value)}
                          className={`bg-zinc-950 border-zinc-800 text-white ${['F1','F2','F3'].includes(formData.category) ? 'opacity-60' : ''}`}
                          placeholder="Auto" readOnly={['F1','F2','F3'].includes(formData.category)} />
                        <p className="text-xs text-zinc-500">
                          {formData.category === 'F1' && 'Fixe : 1m (F1)'}
                          {formData.category === 'F2' && 'Fixe : 8m (F2)'}
                          {formData.category === 'F3' && 'Fixe : 25m (F3)'}
                          {formData.category === 'F4' && 'FDS ou calibre (mm) = distance (m)'}
                        </p>
                      </div>
                    </div>
                  </TabsContent>

                  {/* COMMERCIAL TAB */}
                  <TabsContent value="commercial" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Fabricant</Label>
                        <Input value={formData.manufacturer} onChange={(e) => f('manufacturer', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white" placeholder="Nom du fabricant" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Fournisseur</Label>
                        <Input value={formData.supplier} onChange={(e) => f('supplier', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white" placeholder="Nom du fournisseur" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Pays d'origine</Label>
                      <Input value={formData.country_origin} onChange={(e) => f('country_origin', e.target.value)}
                        className="bg-zinc-950 border-zinc-800 text-white" placeholder="Pays d'origine" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Prix d'achat HT</Label>
                        <Input type="number" step="0.01" value={formData.purchase_price} onChange={(e) => f('purchase_price', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white font-mono" placeholder="Prix HT" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Prix public TTC</Label>
                        <Input type="number" step="0.01" value={formData.public_price} onChange={(e) => f('public_price', e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-white font-mono" placeholder="Prix TTC" />
                      </div>
                    </div>
                  </TabsContent>

                  {/* DOCUMENTS TAB */}
                  <TabsContent value="documents" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">N° Agrément / Homologation</Label>
                      <Input value={formData.homologation_number} onChange={(e) => f('homologation_number', e.target.value)}
                        className="bg-zinc-950 border-zinc-800 text-white font-mono" placeholder="N° d'agrément" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">URL Fiche de Données de Sécurité (FDS)</Label>
                      <Input value={formData.fds_url} onChange={(e) => f('fds_url', e.target.value)}
                        className="bg-zinc-950 border-zinc-800 text-white" placeholder="https://..." />
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                  <Button type="button" variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }} className="border-zinc-700">Annuler</Button>
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="save-product-button">
                    {editMode ? 'Enregistrer' : 'Créer le produit'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters & Sort */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3">
            {/* Search row */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input placeholder="Rechercher par référence, désignation, code ONU..." value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-zinc-950 border-zinc-800 text-white" data-testid="product-search-input" />
              </div>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-zinc-400 hover:text-white whitespace-nowrap" data-testid="clear-filters-button">
                  <X className="w-4 h-4 mr-1" /> Réinitialiser
                </Button>
              )}
            </div>
            {/* Filters row */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-zinc-500 font-semibold uppercase tracking-wider mr-1">Filtres :</span>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[130px] h-8 bg-zinc-950 border-zinc-800 text-white text-xs" data-testid="filter-category">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all" className="text-white">Toutes catég.</SelectItem>
                  {CATEGORIES.map(cat => <SelectItem key={cat} value={cat} className="text-white">{cat}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={riskClassFilter} onValueChange={setRiskClassFilter}>
                <SelectTrigger className="w-[130px] h-8 bg-zinc-950 border-zinc-800 text-white text-xs" data-testid="filter-risk-class">
                  <SelectValue placeholder="Classe ADR" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all" className="text-white">Toutes ADR</SelectItem>
                  {STORAGE_CLASSES.map(cls => <SelectItem key={cls} value={cls} className="text-white">{cls}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[160px] h-8 bg-zinc-950 border-zinc-800 text-white text-xs" data-testid="filter-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all" className="text-white">Tous types</SelectItem>
                  {artificeTypes.map(t => <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>)}
                </SelectContent>
              </Select>

              <div className="h-4 w-px bg-zinc-700 mx-1 hidden sm:block" />

              <span className="text-xs text-zinc-500 font-semibold uppercase tracking-wider mr-1">Tri :</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px] h-8 bg-zinc-950 border-zinc-800 text-white text-xs" data-testid="sort-by-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="reference" className="text-white">Référence</SelectItem>
                  <SelectItem value="designation" className="text-white">Désignation</SelectItem>
                  <SelectItem value="category" className="text-white">Catégorie</SelectItem>
                  <SelectItem value="risk_class" className="text-white">Classe ADR</SelectItem>
                  <SelectItem value="type_artifice" className="text-white">Type d'artifice</SelectItem>
                  <SelectItem value="nec" className="text-white">NEC</SelectItem>
                  <SelectItem value="caliber" className="text-white">Calibre</SelectItem>
                  <SelectItem value="manufacturer" className="text-white">Fabricant</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-8 border-zinc-700 text-zinc-400 hover:text-white px-2"
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')} data-testid="sort-order-toggle">
                {sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                <span className="ml-1 text-xs">{sortOrder === 'asc' ? 'A→Z' : 'Z→A'}</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          <ScrollArea className="h-[calc(100vh-350px)]">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white transition-colors" onClick={() => toggleSort('reference')}>
                    <span className="flex items-center">Référence<SortIcon field="reference" /></span>
                  </TableHead>
                  <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white transition-colors" onClick={() => toggleSort('designation')}>
                    <span className="flex items-center">Désignation<SortIcon field="designation" /></span>
                  </TableHead>
                  <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white transition-colors" onClick={() => toggleSort('type_artifice')}>
                    <span className="flex items-center">Type<SortIcon field="type_artifice" /></span>
                  </TableHead>
                  <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white transition-colors" onClick={() => toggleSort('category')}>
                    <span className="flex items-center">Cat.<SortIcon field="category" /></span>
                  </TableHead>
                  <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white transition-colors" onClick={() => toggleSort('risk_class')}>
                    <span className="flex items-center">ADR<SortIcon field="risk_class" /></span>
                  </TableHead>
                  <TableHead className="text-zinc-400">ONU</TableHead>
                  <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white transition-colors" onClick={() => toggleSort('caliber')}>
                    <span className="flex items-center">Calibre<SortIcon field="caliber" /></span>
                  </TableHead>
                  <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white transition-colors" onClick={() => toggleSort('nec')}>
                    <span className="flex items-center">NEC<SortIcon field="nec" /></span>
                  </TableHead>
                  <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white transition-colors" onClick={() => toggleSort('manufacturer')}>
                    <span className="flex items-center">Fabricant<SortIcon field="manufacturer" /></span>
                  </TableHead>
                  <TableHead className="text-zinc-400">Agrément</TableHead>
                  <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8">
                      <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-zinc-500">
                      <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Aucun produit trouvé</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product) => (
                    <TableRow key={product.id} className={`border-zinc-800 table-row-hover cursor-pointer ${incompleteFilter ? 'bg-red-500/5' : ''}`}
                      onClick={() => handleViewProduct(product)} data-testid={`product-row-${product.id}`}>
                      <TableCell className="font-mono text-cyan-400 text-xs whitespace-nowrap">
                        <span className="flex items-center gap-1.5">
                          {duplicateIds.has(product.id) && (
                            <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse flex-shrink-0" title="Doublon potentiel" />
                          )}
                          {(!product.risk_class || !product.un_code || !product.nec || !product.homologation_number || !product.category) && (
                            <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" title="Fiche incomplete" />
                          )}
                          {product.reference}
                        </span>
                      </TableCell>
                      <TableCell className={`max-w-[180px] truncate text-sm ${!product.designation ? 'text-red-400 italic' : 'text-white'}`}>{product.designation || 'MANQUANT'}</TableCell>
                      <TableCell className="text-zinc-400 text-xs whitespace-nowrap">{product.type_artifice || '-'}</TableCell>
                      <TableCell>
                        {product.category ? (
                          <Badge variant="outline" className={`text-xs ${getCategoryBadgeClass(product.category)}`}>{product.category}</Badge>
                        ) : <span className="text-red-400 text-xs italic">Manquant</span>}
                      </TableCell>
                      <TableCell>
                        {product.risk_class ? (
                          <Badge variant="outline" className={`text-xs ${getRiskBadgeClass(product.risk_class)}`}>{product.risk_class}</Badge>
                        ) : <span className="text-red-400 text-xs italic">Manquant</span>}
                      </TableCell>
                      <TableCell className={`font-mono text-xs ${!product.un_code ? 'text-red-400 italic' : 'text-zinc-400'}`}>{product.un_code || 'Manquant'}</TableCell>
                      <TableCell className="text-zinc-400 text-xs">
                        {product.caliber ? `${product.caliber}mm` : '-'}
                      </TableCell>
                      <TableCell className={`font-mono text-xs ${!product.nec ? 'text-red-400 italic' : 'text-amber-400'}`}>
                        {product.nec ? `${Math.round(product.nec * 1000 * 100) / 100} g` : 'Manquant'}
                      </TableCell>
                      <TableCell className="text-zinc-400 text-xs whitespace-nowrap">{product.manufacturer || '-'}</TableCell>
                      <TableCell className={`font-mono text-xs max-w-[120px] truncate ${!product.homologation_number ? 'text-red-400 italic' : 'text-green-400'}`}>{product.homologation_number || 'Manquant'}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                            <DropdownMenuItem className="text-white cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); handleViewProduct(product); }}>
                              <Eye className="w-4 h-4 mr-2" /> Voir
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-white cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); handleEditProduct(product); }}>
                              <Edit className="w-4 h-4 mr-2" /> Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-white cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); handleDownloadQR(product); }}>
                              <QrCode className="w-4 h-4 mr-2" /> QR Code
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-white cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); setLabelItems([{ product_id: product.id, reference: product.reference, designation: product.designation, risk_class: product.risk_class, quantity: 1 }]); setShowLabelDialog(true); }}>
                              <Printer className="w-4 h-4 mr-2" /> Étiquette
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-white cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); handleViewHistory(product.id); }}>
                              <History className="w-4 h-4 mr-2" /> Historique
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-red-400 cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); handleDeleteProduct(product.id); }}>
                              <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800" data-testid="products-pagination">
              <p className="text-sm text-zinc-400">
                {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, total)} sur {total} produits
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => setPage(1)}
                  className="border-zinc-700 text-zinc-400 hover:text-white h-8 px-2 text-xs" data-testid="pagination-first">
                  &laquo;
                </Button>
                <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="border-zinc-700 text-zinc-400 hover:text-white h-8 px-2 text-xs" data-testid="pagination-prev">
                  &lsaquo; Préc.
                </Button>
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (page <= 3) pageNum = i + 1;
                  else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = page - 2 + i;
                  return (
                    <Button key={pageNum} variant={pageNum === page ? 'default' : 'outline'} size="sm"
                      onClick={() => setPage(pageNum)}
                      className={`h-8 w-8 px-0 text-xs ${
                        pageNum === page 
                          ? 'bg-amber-500 text-black hover:bg-amber-400 border-amber-500' 
                          : 'border-zinc-700 text-zinc-400 hover:text-white'
                      }`} data-testid={`pagination-page-${pageNum}`}>
                      {pageNum}
                    </Button>
                  );
                })}
                <Button variant="outline" size="sm" disabled={page >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  className="border-zinc-700 text-zinc-400 hover:text-white h-8 px-2 text-xs" data-testid="pagination-next">
                  Suiv. &rsaquo;
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages}
                  onClick={() => setPage(totalPages)}
                  className="border-zinc-700 text-zinc-400 hover:text-white h-8 px-2 text-xs" data-testid="pagination-last">
                  &raquo;
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Label Print Dialog */}
      <LabelPrintDialog
        open={showLabelDialog}
        onOpenChange={setShowLabelDialog}
        items={labelItems}
        products={products}
      />

      {/* Duplicates Detection Dialog */}
      <Dialog open={showDuplicates} onOpenChange={setShowDuplicates}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="duplicates-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Copy className="w-5 h-5 text-orange-400" />
              Doublons potentiels
              {duplicateData && (
                <Badge variant="outline" className="ml-2 text-xs bg-orange-500/20 text-orange-400 border-orange-500/30">
                  {duplicateData.total_duplicates} produits dans {duplicateData.groups.length} groupes
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {duplicateData && duplicateData.groups.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-60" />
              <p className="text-green-400 font-medium">Aucun doublon</p>
              <p className="text-zinc-500 text-sm mt-1">Tous les articles sont uniques</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-zinc-500 text-xs">
                Produits partageant la meme reference ou la meme designation (apres normalisation).
                Verifiez manuellement et supprimez les doublons si necessaire.
              </p>
              {duplicateData?.groups.map((group, gi) => (
                <Card key={gi} className="bg-zinc-950/70 border-zinc-800">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className={`text-[10px] ${group.match_type === 'reference' ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' : 'bg-purple-500/20 text-purple-400 border-purple-500/30'}`}>
                        {group.match_type === 'reference' ? 'REF' : 'DESIG'}
                      </Badge>
                      <span className="text-white text-sm font-medium truncate">{group.match_value}</span>
                      <Badge variant="outline" className="text-[10px] bg-orange-500/20 text-orange-400 border-orange-500/30 ml-auto">
                        {group.products.length} prod.
                      </Badge>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800 hover:bg-transparent">
                          <TableHead className="text-zinc-500 text-[10px] py-1 h-auto">Reference</TableHead>
                          <TableHead className="text-zinc-500 text-[10px] py-1 h-auto">Designation</TableHead>
                          <TableHead className="text-zinc-500 text-[10px] py-1 h-auto">Cat.</TableHead>
                          <TableHead className="text-zinc-500 text-[10px] py-1 h-auto">ADR</TableHead>
                          <TableHead className="text-zinc-500 text-[10px] py-1 h-auto">Calibre</TableHead>
                          <TableHead className="text-zinc-500 text-[10px] py-1 h-auto">NEC</TableHead>
                          <TableHead className="text-zinc-500 text-[10px] py-1 h-auto">Fabricant</TableHead>
                          <TableHead className="text-zinc-500 text-[10px] py-1 h-auto">Agrement</TableHead>
                          <TableHead className="text-zinc-500 text-[10px] py-1 h-auto text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.products.map((p) => (
                          <TableRow key={p.id} className="border-zinc-800/50">
                            <TableCell className="font-mono text-cyan-400 text-xs py-1.5">{p.reference}</TableCell>
                            <TableCell className="text-white text-xs py-1.5 max-w-[200px] truncate">{p.designation}</TableCell>
                            <TableCell className="py-1.5">
                              <Badge variant="outline" className={`text-[10px] ${getCategoryBadgeClass(p.category)}`}>{p.category || '-'}</Badge>
                            </TableCell>
                            <TableCell className="py-1.5">
                              {p.risk_class && <Badge variant="outline" className={`text-[10px] ${getRiskBadgeClass(p.risk_class)}`}>{p.risk_class}</Badge>}
                            </TableCell>
                            <TableCell className="text-zinc-400 text-xs py-1.5">{p.caliber ? `${p.caliber}mm` : '-'}</TableCell>
                            <TableCell className="font-mono text-amber-400 text-xs py-1.5">{p.nec ? `${Math.round(p.nec * 1000 * 100) / 100} g` : '-'}</TableCell>
                            <TableCell className="text-zinc-400 text-xs py-1.5">{p.manufacturer || '-'}</TableCell>
                            <TableCell className="font-mono text-green-400 text-[10px] py-1.5">{p.homologation_number || '-'}</TableCell>
                            <TableCell className="text-right py-1.5">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-zinc-400 hover:text-white"
                                  onClick={() => { setShowDuplicates(false); handleViewProduct(p); }} data-testid={`dup-view-${p.id}`}>
                                  <Eye className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-zinc-400 hover:text-white"
                                  onClick={() => { setShowDuplicates(false); handleEditProduct(p); }} data-testid={`dup-edit-${p.id}`}>
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                                  onClick={() => { handleDeleteProduct(p.id); fetchDuplicates(); }} data-testid={`dup-delete-${p.id}`}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage Artifice Types Dialog */}
      <Dialog open={showManageTypes} onOpenChange={setShowManageTypes}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="manage-types-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Types d'artifice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-white flex-1"
                placeholder="Nouveau type..."
                data-testid="new-type-input"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddArtificeType())} />
              <Button onClick={handleAddArtificeType} className="bg-amber-500 hover:bg-amber-400 text-black"
                data-testid="add-type-button" disabled={!newTypeName.trim()}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <ScrollArea className="h-[300px]">
              <div className="space-y-1">
                {artificeTypes.length === 0 ? (
                  <p className="text-zinc-500 text-sm text-center py-4">Aucun type défini</p>
                ) : artificeTypes.map((t) => (
                  <div key={t} className="flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-800 group"
                    data-testid={`type-item-${t}`}>
                    <span className="text-sm text-white">{t}</span>
                    <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 h-7 w-7 p-0"
                      onClick={() => handleDeleteArtificeType(t)} data-testid={`delete-type-${t}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <History className="w-5 h-5 text-amber-400" />
              Historique — {historyData?.product?.reference || ''}
            </DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : historyData && (
            <div className="space-y-4">
              {/* Stock locations */}
              <div>
                <h3 className="text-sm font-semibold text-cyan-400 mb-2">Stock actuel ({historyData.stock_entries?.length || 0} emplacement(s))</h3>
                {(historyData.stock_entries || []).length === 0 ? (
                  <p className="text-zinc-500 text-xs">Aucun stock</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {historyData.stock_entries.map((s, i) => (
                      <div key={i} className="bg-zinc-950 border border-zinc-800 rounded p-2">
                        <p className="text-white text-sm font-mono">{s.available_quantity} unité(s)</p>
                        <p className="text-zinc-500 text-[10px]">Dépôt: {s.depot_id?.substring(0, 12)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Preparations */}
              <div>
                <h3 className="text-sm font-semibold text-fuchsia-400 mb-2">Préparations ({historyData.preparations?.length || 0})</h3>
                {(historyData.preparations || []).length === 0 ? (
                  <p className="text-zinc-500 text-xs">Aucune préparation</p>
                ) : (
                  <div className="space-y-1">
                    {historyData.preparations.map((p, i) => (
                      <div key={i} className="flex items-center justify-between bg-zinc-950 rounded px-3 py-1.5 text-xs">
                        <span className="text-zinc-300 font-mono">{p.id}</span>
                        <span className="text-zinc-400">{p.client || '-'}</span>
                        <span className={`${p.status === 'shipped' ? 'text-green-400' : 'text-amber-400'}`}>{p.status}</span>
                        <span className="text-zinc-500">{p.created_at?.substring(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Receptions */}
              <div>
                <h3 className="text-sm font-semibold text-green-400 mb-2">Réceptions ({historyData.receptions?.length || 0})</h3>
                {(historyData.receptions || []).length === 0 ? (
                  <p className="text-zinc-500 text-xs">Aucune réception</p>
                ) : (
                  <div className="space-y-1">
                    {historyData.receptions.map((r, i) => (
                      <div key={i} className="flex items-center justify-between bg-zinc-950 rounded px-3 py-1.5 text-xs">
                        <span className="text-zinc-300 font-mono">{r.id}</span>
                        <span className="text-zinc-400">{r.supplier || '-'}</span>
                        <span className="text-green-400">{r.status}</span>
                        <span className="text-zinc-500">{r.created_at?.substring(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Movements */}
              <div>
                <h3 className="text-sm font-semibold text-amber-400 mb-2">Mouvements ({historyData.movements?.length || 0})</h3>
                {(historyData.movements || []).length === 0 ? (
                  <p className="text-zinc-500 text-xs">Aucun mouvement enregistré</p>
                ) : (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {historyData.movements.map((m, i) => (
                      <div key={i} className="flex items-center justify-between bg-zinc-950 rounded px-3 py-1.5 text-xs">
                        <span className={`font-mono ${m.movement_type === 'entry' ? 'text-green-400' : 'text-red-400'}`}>
                          {m.movement_type === 'entry' ? '+' : '-'}{m.quantity}
                        </span>
                        <span className="text-zinc-400">{m.movement_type}</span>
                        <span className="text-zinc-500">{m.created_at?.substring(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Product Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-400" />
              {selectedProduct?.designation}
            </DialogTitle>
          </DialogHeader>
          {selectedProduct && (
            <div className="space-y-6">
              {/* Top badges */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-xl text-cyan-400">{selectedProduct.reference}</span>
                <Badge variant="outline" className={`text-sm px-3 py-1 ${getCategoryBadgeClass(selectedProduct.category)}`}>
                  {selectedProduct.category}
                </Badge>
                {selectedProduct.risk_class && (
                  <Badge variant="outline" className={`text-sm px-3 py-1 ${getRiskBadgeClass(selectedProduct.risk_class)}`}>
                    {selectedProduct.risk_class}
                  </Badge>
                )}
                {selectedProduct.type_artifice && (
                  <Badge variant="outline" className="text-sm px-3 py-1 bg-zinc-800 text-zinc-300 border-zinc-700">
                    {selectedProduct.type_artifice}
                  </Badge>
                )}
              </div>

              {/* Réglementaire */}
              <div>
                <h4 className="text-xs text-amber-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Shield className="w-3 h-3" /> Réglementaire
                </h4>
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-zinc-950 rounded-lg p-3 border border-zinc-800">
                    <p className="text-xs text-zinc-500 mb-0.5">Code ONU</p>
                    <p className="font-mono text-lg text-red-400">{selectedProduct.un_code || '-'}</p>
                  </div>
                  <div className="bg-zinc-950 rounded-lg p-3 border border-zinc-800">
                    <p className="text-xs text-zinc-500 mb-0.5">NEC</p>
                    <p className="font-mono text-lg text-amber-400">{selectedProduct.nec ? `${Math.round(selectedProduct.nec * 1000 * 100) / 100} g` : '-'}</p>
                  </div>
                  <div className="bg-zinc-950 rounded-lg p-3 border border-zinc-800">
                    <p className="text-xs text-zinc-500 mb-0.5">Classe ADR</p>
                    <p className="text-lg text-white">{selectedProduct.risk_class || '-'}</p>
                  </div>
                  <div className="bg-zinc-950 rounded-lg p-3 border border-zinc-800">
                    <p className="text-xs text-zinc-500 mb-0.5">N° Agrément</p>
                    <p className="font-mono text-sm text-green-400">{selectedProduct.homologation_number || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Technique pyro */}
              <div>
                <h4 className="text-xs text-cyan-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Flame className="w-3 h-3" /> Caractéristiques pyrotechniques
                </h4>
                <div className="grid grid-cols-4 gap-4">
                  <DetailRow label="Calibre" value={selectedProduct.caliber ? `${selectedProduct.caliber} mm` : null} mono />
                  <DetailRow label="Nombre de coups" value={selectedProduct.shots_count} mono />
                  <DetailRow label="Durée" value={selectedProduct.duration ? `${selectedProduct.duration} s` : null} mono />
                  <DetailRow label="Distance sécurité" value={selectedProduct.safety_distance ? `${selectedProduct.safety_distance} m` : null} mono />
                </div>
              </div>

              {/* Poids & dimensions */}
              <div>
                <h4 className="text-xs text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Weight className="w-3 h-3" /> Poids & dimensions
                </h4>
                <div className="grid grid-cols-4 gap-4">
                  <DetailRow label="Poids brut" value={selectedProduct.gross_weight ? `${Math.round(selectedProduct.gross_weight * 1000 * 100) / 100} g` : null} mono />
                  <DetailRow label="Poids net" value={selectedProduct.net_weight ? `${selectedProduct.net_weight} kg` : null} mono />
                  <DetailRow label="Conditionnement" value={selectedProduct.packaging} />
                  <DetailRow label="Dimensions" value={selectedProduct.dimensions} />
                </div>
              </div>

              {/* Commercial */}
              <div>
                <h4 className="text-xs text-green-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <DollarSign className="w-3 h-3" /> Informations commerciales
                </h4>
                <div className="grid grid-cols-4 gap-4">
                  <DetailRow label="Fabricant" value={selectedProduct.manufacturer} />
                  <DetailRow label="Fournisseur" value={selectedProduct.supplier} />
                  <DetailRow label="Prix achat HT" value={selectedProduct.purchase_price ? `${selectedProduct.purchase_price.toFixed(2)} €` : null} mono accent="text-green-400" />
                  <DetailRow label="Prix public TTC" value={selectedProduct.public_price ? `${selectedProduct.public_price.toFixed(2)} €` : null} mono accent="text-white" />
                </div>
              </div>

              {/* Stock */}
              {selectedProduct.stock && (
                <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/30">
                  <p className="text-xs text-amber-400 uppercase tracking-wider mb-2">Stock actuel</p>
                  <div className="flex items-baseline gap-4">
                    <span className="text-3xl font-bold text-white">{selectedProduct.stock.available_quantity || 0}</span>
                    <span className="text-zinc-400">disponibles</span>
                    {selectedProduct.stock.reserved_quantity > 0 && (
                      <span className="text-zinc-500">({selectedProduct.stock.reserved_quantity} réservés)</span>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 border-zinc-700" onClick={() => { setShowViewDialog(false); handleEditProduct(selectedProduct); }}>
                  <Edit className="w-4 h-4 mr-2" /> Modifier
                </Button>
                <Button variant="outline" className="flex-1 border-zinc-700" onClick={() => handleDownloadQR(selectedProduct)}>
                  <QrCode className="w-4 h-4 mr-2" /> QR Code
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Auto-map ADR risk class Dialog */}
      <Dialog open={showAutoMap} onOpenChange={(v) => { setShowAutoMap(v); if (!v) setAutoMapPreview(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="auto-map-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Zap className="w-5 h-5 text-cyan-400" /> Application automatique de la classe ADR
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-zinc-950/60 border border-zinc-800 rounded p-3 text-xs text-zinc-400 leading-relaxed">
              Règle pyrotechnique officielle : la <span className="text-white">classe ADR</span> découle directement du <span className="text-white">code ONU</span>.
              Exemples : <span className="text-amber-400 font-mono">UN0333 → 1.1G</span>, <span className="text-amber-400 font-mono">UN0334 → 1.2G</span>, <span className="text-amber-400 font-mono">UN0335 → 1.3G</span>, <span className="text-amber-400 font-mono">UN0336 → 1.4G</span>.
              Cette action parcourt tous les articles ayant un code ONU et force la classe ADR conforme à la réglementation.
            </div>

            {autoMapLoading && (
              <div className="flex items-center justify-center py-10">
                <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {autoMapPreview && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-zinc-950 border border-zinc-800 rounded p-3">
                    <p className="text-[11px] text-zinc-500 uppercase tracking-wide">Analysés</p>
                    <p className="text-2xl font-bold text-white mt-1">{autoMapPreview.total_scanned}</p>
                  </div>
                  <div className="bg-cyan-500/5 border border-cyan-500/30 rounded p-3">
                    <p className="text-[11px] text-cyan-400 uppercase tracking-wide">À mettre à jour</p>
                    <p className="text-2xl font-bold text-cyan-400 mt-1">{autoMapPreview.total_to_update}</p>
                  </div>
                  <div className="bg-zinc-950 border border-zinc-800 rounded p-3">
                    <p className="text-[11px] text-zinc-500 uppercase tracking-wide">Codes UN mappés</p>
                    <p className="text-2xl font-bold text-white mt-1">{Object.keys(autoMapPreview.un_mapping || {}).length}</p>
                  </div>
                </div>

                {autoMapPreview.total_to_update === 0 ? (
                  <div className="bg-green-500/10 border border-green-500/30 rounded p-4 text-center">
                    <p className="text-green-400 font-medium">Aucun changement nécessaire</p>
                    <p className="text-xs text-zinc-400 mt-1">Tous les articles ayant un code ONU mappé sont déjà conformes à la règle ADR.</p>
                  </div>
                ) : (
                  <div className="border border-zinc-800 rounded overflow-hidden">
                    <div className="max-h-[45vh] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-zinc-800 hover:bg-transparent sticky top-0 bg-zinc-900 z-10">
                            <TableHead className="text-zinc-400 text-xs">Référence</TableHead>
                            <TableHead className="text-zinc-400 text-xs">Désignation</TableHead>
                            <TableHead className="text-zinc-400 text-xs">Code ONU</TableHead>
                            <TableHead className="text-zinc-400 text-xs text-center">Avant</TableHead>
                            <TableHead className="text-zinc-400 text-xs text-center">Après</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(autoMapPreview.updates || []).map((u, i) => (
                            <TableRow key={i} className="border-zinc-800 hover:bg-zinc-800/30">
                              <TableCell className="font-mono text-cyan-400 text-xs">{u.reference}</TableCell>
                              <TableCell className="text-zinc-300 text-xs max-w-[250px] truncate">{u.designation}</TableCell>
                              <TableCell className="font-mono text-amber-400 text-xs">{u.un_code}</TableCell>
                              <TableCell className="text-center">
                                {u.old_risk_class ? (
                                  <Badge variant="outline" className="text-[10px] border-red-500/40 text-red-400 line-through">{u.old_risk_class}</Badge>
                                ) : (
                                  <span className="text-zinc-600 text-xs italic">vide</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">{u.new_risk_class}</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {autoMapPreview.truncated && (
                      <p className="text-[11px] text-zinc-500 text-center py-2 border-t border-zinc-800">
                        Aperçu limité aux 500 premiers résultats. L'application prendra en compte la totalité.
                      </p>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-3 border-t border-zinc-800">
                  <Button variant="outline" className="border-zinc-700" onClick={() => setShowAutoMap(false)} data-testid="auto-map-cancel">
                    Annuler
                  </Button>
                  <Button
                    className="bg-cyan-500 hover:bg-cyan-400 text-black"
                    onClick={applyAutoMap}
                    disabled={autoMapPreview.total_to_update === 0 || autoMapApplying}
                    data-testid="auto-map-apply">
                    {autoMapApplying ? (
                      <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" /> Application...</>
                    ) : (
                      <><Zap className="w-4 h-4 mr-2" /> Appliquer à {autoMapPreview.total_to_update} article(s)</>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
